import { IconButton, Dialog as UIDialog } from '@/shared/ui-kit/components'
import { XIcon } from 'lucide-react'
import type { ComponentProps } from 'react'
import { DialogPropsContext, useDialogProps } from './props-context'
import type { DialogProps } from './types'

const sizeMap = {
  sm: { maxWidth: '400px', width: '90%' },
  md: { maxWidth: '600px', width: '90%' },
  lg: { maxWidth: '800px', width: '90%' },
  xl: { maxWidth: '1200px', width: '95%' },
}

export const Dialog = (props: DialogProps) => {
  const {
    isOpen,
    onClose,
    children,
    showCloseButton = true,
    closable = true,
    size = 'md',
    width,
    style,
    css,
    positionerClassname,
    closeTriggerClassName,
  } = props
  const sizeStyles = sizeMap[size]

  return (
    <DialogPropsContext.Provider value={props}>
      <UIDialog.Root
        open={isOpen}
        onOpenChange={(details) => {
          if (!closable) return
          if (!details.open) {
            onClose()
          }
        }}
        closeOnInteractOutside={closable}
        closeOnEscape={closable}
      >
        <UIDialog.Backdrop />
        <UIDialog.Positioner className={positionerClassname}>
          <UIDialog.Content
            style={{
              ...sizeStyles,
              width,
              ...style,
            }}
            css={css}
            rounded="xl"
            shadow="sm"
            bg="#0E0E0E"
            color="fg.default"
            position="relative"
            border="1px solid"
            borderColor="border.default"
            pointerEvents="auto"
            maxH="90vh"
            overflow="auto"
          >
            {children}
            {showCloseButton && closable && (
              <UIDialog.CloseTrigger
                asChild
                position="absolute"
                top="3"
                right="3"
              >
                <IconButton
                  aria-label="Close dialog"
                  variant="ghost"
                  size="sm"
                  color="fg.muted"
                  className={closeTriggerClassName}
                  _hover={{ color: 'fg.default' }}
                >
                  <XIcon size={16} />
                </IconButton>
              </UIDialog.CloseTrigger>
            )}
          </UIDialog.Content>
        </UIDialog.Positioner>
      </UIDialog.Root>
    </DialogPropsContext.Provider>
  )
}

Dialog.Content = (props: ComponentProps<typeof UIDialog.Content>) => {
  const rootProps = useDialogProps()
  const sizeStyles = sizeMap[rootProps.size!]

  return (
    <UIDialog.Content
      style={{
        ...sizeStyles,
      }}
      rounded="xl"
      shadow="sm"
      p={0}
      bg="#0E0E0E"
      color="fg.default"
      position="relative"
      border="1px solid"
      borderColor="border.default"
      {...props}
    />
  )
}
